"use client";

import React from "react";
import Link from "next/link";
import { Pencil } from "lucide-react";
import { TradeItem } from "modules/tradeLists/model/tradeList";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { truncateText } from "utils/utils";
import { PortalTooltip } from "modules/common/components/PortalTooltip";
import { useFacetSearchParamsContract } from "modules/common/hooks/useFacetSearchParamsContract";
import { binderSectionForTradeListType } from "modules/tradeLists/utils/tradeListRoutes";

interface TradeItemListingLinkProps {
  item: TradeItem;
  size?: "small" | "large";
  iconSize?: number;
  /** Skip the horizontal divider rendered above the large-variant link. */
  noDivider?: boolean;
}

export function TradeItemListingLink({
  item,
  size = "large",
  iconSize = ICON_SIZE,
  noDivider = false,
}: TradeItemListingLinkProps) {
  const { serializeBinderParams } = useFacetSearchParamsContract();

  // Orphaned precompute rows outlive their deleted trade-item and carry no listing to link to.
  if (!item.list) return null;

  const truncatedName = truncateText(item.list.name, 12);

  const tradeListHref = `/x/binder${serializeBinderParams({
    section: binderSectionForTradeListType(item.list.type),
    listId: item.list.id,
    query: item.name,
  })}`;

  if (size === "small") {
    return (
      <PortalTooltip
        content={
          <div className="tooltip bg-primary tooltip-primary text-primary-content rounded-sm px-2 py-1 text-sm">
            Your listing in {truncatedName}
          </div>
        }
        placement="top"
      >
        <Link
          href={tradeListHref}
          className="btn btn-circle btn-sm btn-soft btn-primary"
          aria-label={`Your listing in ${truncatedName}`}
        >
          <Pencil size={iconSize} />
        </Link>
      </PortalTooltip>
    );
  }

  return (
    <>
      {!noDivider && <div className="divider -my-0.5" />}
      <div className="flex grow justify-between">
        <PortalTooltip
          className="flex grow"
          content={
            <div className="tooltip bg-primary tooltip-primary text-primary-content rounded-sm px-2 py-1 text-sm">
              Your listing in {item.list.name}
            </div>
          }
          placement="top"
        >
          <Link
            href={tradeListHref}
            className="btn btn-sm btn-soft btn-primary grow truncate"
            aria-label={`Your listing in ${truncatedName}`}
          >
            <Pencil size={iconSize} />
            {truncateText(item.list.name, 12)}
          </Link>
        </PortalTooltip>
      </div>
    </>
  );
}
